import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Label } from '../ui/label';

/**
 * Canonical list of fields on the Patient record that can be auto-filled
 * into a form field via `patient_field_ref`.  Keys MUST match the keys on
 * the backend PatientCreate/Patient model (see server.py around line 150).
 *
 * Grouped so the dropdown is scannable rather than a flat 30-item list.
 */
export const PATIENT_FIELD_GROUPS = [
  {
    group: 'Identity',
    fields: [
      { key: 'first_name', label: 'First Name' },
      { key: 'last_name', label: 'Last Name' },
      { key: 'date_of_birth', label: 'Date of Birth' },
      { key: 'gender', label: 'Gender' },
      { key: 'mrn', label: 'MRN (Medical Record Number)' },
      { key: 'primary_language', label: 'Primary Language' },
    ],
  },
  {
    group: 'Contact',
    fields: [
      { key: 'email', label: 'Email' },
      { key: 'phone', label: 'Phone' },
      { key: 'address', label: 'Address' },
    ],
  },
  {
    group: 'Insurance',
    fields: [
      { key: 'insurance_provider', label: 'Insurance Provider' },
      { key: 'insurance_id', label: 'Insurance ID' },
    ],
  },
  {
    group: 'Emergency Contact',
    fields: [
      { key: 'emergency_contact_name', label: 'Emergency Contact — Name' },
      { key: 'emergency_contact_phone', label: 'Emergency Contact — Phone' },
      { key: 'emergency_contact_relationship', label: 'Emergency Contact — Relationship' },
    ],
  },
  {
    group: 'Clinical',
    fields: [
      { key: 'medical_practitioner', label: 'Medical Practitioner' },
      { key: 'medical_diagnosis', label: 'Diagnosis' },
      { key: 'allergies', label: 'Allergies' },
      { key: 'medical_history', label: 'Medical History' },
      { key: 'chief_concern', label: 'Chief Concern' },
      { key: 'reason_for_visit', label: 'Reason for Visit' },
      { key: 'last_visit_date', label: 'Last Visit Date' },
    ],
  },
  {
    group: 'Hospital / Admission',
    fields: [
      { key: 'hospital_name', label: 'Hospital Name' },
      { key: 'admission_date', label: 'Admission Date' },
      { key: 'discharge_date', label: 'Discharge Date' },
    ],
  },
  {
    group: 'Pharmacy',
    fields: [
      { key: 'pharmacy_name', label: 'Pharmacy Name' },
      { key: 'pharmacy_address', label: 'Pharmacy Address' },
      { key: 'pharmacy_phone', label: 'Pharmacy Phone' },
    ],
  },
  {
    group: 'Primary Caregiver',
    fields: [
      { key: 'primary_caregiver_name', label: 'Primary Caregiver — Name' },
      { key: 'primary_caregiver_relationship', label: 'Primary Caregiver — Relationship' },
      { key: 'primary_caregiver_phone', label: 'Primary Caregiver — Phone' },
      { key: 'primary_caregiver_address', label: 'Primary Caregiver — Address' },
    ],
  },
  {
    group: 'Other',
    fields: [
      { key: 'other_agencies', label: 'Other Agencies' },
    ],
  },
  {
    group: 'Official Form Boxes',
    fields: [
      { key: 'full_name', label: 'Patient Full Name' },
      { key: 'name_and_address', label: 'Patient Name + Address (box)' },
      { key: 'sex_code', label: 'Sex (M / F)' },
      { key: 'is_male', label: 'Sex — Male checkbox' },
      { key: 'is_female', label: 'Sex — Female checkbox' },
      { key: 'hi_claim_no', label: "Patient's HI Claim No. (Insurance ID)" },
      { key: 'principal_diagnosis', label: 'Principal Diagnosis' },
      { key: 'medications_list', label: 'Active Medications (read-only)' },
      { key: 'today', label: "Today's Date (read-only)" },
    ],
  },
  {
    group: 'Agency (read-only)',
    fields: [
      { key: 'agency_name', label: 'Agency Name' },
      { key: 'agency_address', label: 'Agency Address' },
      { key: 'agency_phone', label: 'Agency Phone' },
      { key: 'agency_fax', label: 'Agency Fax' },
      { key: 'agency_provider_number', label: 'Agency Provider No.' },
      { key: 'agency_name_address_phone', label: 'Agency Name, Address & Phone (box)' },
    ],
  },
];

// Flattened list for O(1) lookup by key
export const PATIENT_FIELDS_BY_KEY = Object.fromEntries(
  PATIENT_FIELD_GROUPS.flatMap(g => g.fields.map(f => [f.key, { ...f, group: g.group }]))
);

/**
 * Dropdown picker for selecting a patient field reference.  Pulls the
 * organization's custom patient fields from the backend too (if the user has
 * permission), appending them under a "Custom fields" group.
 *
 * Props:
 *   value       — current `patient_field_ref` string
 *   onChange    — (key|null) => void
 *   testIdPrefix
 */
export default function PatientFieldRefPicker({ value, onChange, testIdPrefix = 'patient-field-ref' }) {
  const [customFields, setCustomFields] = useState([]);

  useEffect(() => {
    let alive = true;
    api.get('/patient-custom-fields')
      .then(res => { if (alive) setCustomFields(res.data?.fields || []); })
      .catch(() => { /* non-admins get 403 — silently ignore, canonical list is enough */ });
    return () => { alive = false; };
  }, []);

  // The Shadcn Select requires a non-empty value for "none" — use a sentinel.
  const selectValue = value || '__none__';

  return (
    <div>
      <Label className="text-[10px] text-[#94A3B8]">Reference Patient Field (optional)</Label>
      <Select
        value={selectValue}
        onValueChange={(v) => onChange(v === '__none__' ? null : v)}
      >
        <SelectTrigger className="h-7 text-[11px]" data-testid={`${testIdPrefix}-trigger`}>
          <SelectValue placeholder="Select a patient field…" />
        </SelectTrigger>
        <SelectContent className="max-h-[340px]">
          <SelectItem value="__none__" className="text-[11px]">
            — None (don&apos;t pre-fill) —
          </SelectItem>
          {PATIENT_FIELD_GROUPS.map((g) => (
            <div key={g.group}>
              <div className="px-2 pt-2 pb-1 text-[9px] uppercase tracking-wider font-semibold text-[#64748B] bg-[#F8FAFC]">
                {g.group}
              </div>
              {g.fields.map((f) => (
                <SelectItem key={f.key} value={f.key} className="text-[11px]" data-testid={`${testIdPrefix}-option-${f.key}`}>
                  {f.label}
                  <span className="ml-2 text-[9px] text-[#94A3B8] font-mono">{f.key}</span>
                </SelectItem>
              ))}
            </div>
          ))}
          {customFields.length > 0 && (
            <div>
              <div className="px-2 pt-2 pb-1 text-[9px] uppercase tracking-wider font-semibold text-[#7C3AED] bg-[#F5F3FF]">
                Custom fields
              </div>
              {customFields.map((f) => (
                <SelectItem key={f.key} value={f.key} className="text-[11px]" data-testid={`${testIdPrefix}-option-custom-${f.key}`}>
                  {f.label}
                  <span className="ml-2 text-[9px] text-[#94A3B8] font-mono">{f.key}</span>
                </SelectItem>
              ))}
            </div>
          )}
        </SelectContent>
      </Select>
    </div>
  );
}
